import React from "react";
import { ReferralEventType, ReferralStats, formatMoney } from "../utils/referral";

const LABELS: Record<ReferralEventType, string> = {
  applied: "Applications",
  approved: "Approvals",
  code_changed: "Code changes",
  suspended: "Pauses",
  resumed: "Resumes",
  removed: "Removals",
  signup: "Accounts signed up",
  signup_removed: "Accounts removed",
  payment: "Payments recorded",
};
const SENTENCE: Record<ReferralEventType, string> = {
  applied: "applied", approved: "was approved", code_changed: "had the code changed", suspended: "was paused", resumed: "was resumed",
  removed: "was removed", signup: "gained an account", signup_removed: "lost an account", payment: "had earnings recorded",
};
const SUMMARY: ReferralEventType[] = ["applied", "approved", "signup", "payment", "suspended", "removed"];

const card = "bg-white rounded-3xl p-5 shadow-sm border border-slate-100";

/** What happened in the partner program lately: partners, sign-ups per day, the top partners and the latest events. */
const ReferralDashboard: React.FC<{ stats: ReferralStats }> = ({ stats }) => {
  const peak = Math.max(1, ...stats.series.map((d) => d.signup));
  return (
    <section className="space-y-4" aria-label="Program dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={card}><div className="text-3xl font-bold text-slate-900">{stats.partners.approved}</div><p className="text-slate-600 text-sm">Active partners</p></div>
        <div className={card}><div className="text-3xl font-bold text-slate-900">{stats.partners.pending}</div><p className="text-slate-600 text-sm">Waiting for approval</p></div>
        <div className={card}><div className="text-3xl font-bold text-slate-900">{stats.signups}</div><p className="text-slate-600 text-sm">Accounts referred, all time</p></div>
        <div className={card}><div className="text-3xl font-bold text-slate-900">{formatMoney(stats.earned)}</div><p className="text-slate-600 text-sm">Partner earnings, all time</p></div>
      </div>

      <div className={card}>
        <h2 className="text-lg font-bold text-slate-900">Last {stats.days} days</h2>
        <dl className="mt-2 grid grid-cols-3 md:grid-cols-6 gap-3">
          {SUMMARY.map((t) => (
            <div key={t}>
              <dt className="text-slate-600 text-xs">{LABELS[t]}</dt>
              <dd className="text-xl font-bold text-slate-900">{stats.inPeriod[t] ?? 0}</dd>
            </div>
          ))}
        </dl>
        <p className="text-slate-600 text-xs mt-4">Accounts signed up per day (most in a day: {peak === 1 && stats.series.every((d) => d.signup === 0) ? 0 : peak})</p>
        <div className="flex items-end gap-0.5 h-20 mt-1" role="img" aria-label={`Accounts signed up per day over the last ${stats.days} days: ${stats.inPeriod.signup ?? 0} in total`}>
          {stats.series.map((d) => (
            <div key={d.date} title={`${d.date}: ${d.signup}`} className="flex-1 bg-blue-100 rounded-t min-h-[2px] relative" style={{ height: "100%" }}>
              <div className="absolute bottom-0 inset-x-0 bg-blue-600 rounded-t" style={{ height: `${(d.signup / peak) * 100}%`, minHeight: d.signup ? 2 : 0 }} />
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className={card}>
          <h2 className="text-lg font-bold text-slate-900">Top partners</h2>
          {stats.top.length === 0 ? <p className="text-slate-600 text-sm mt-2">No partners yet.</p> : (
            <table className="w-full text-sm mt-2">
              <thead><tr className="text-left text-slate-600"><th className="font-normal">Partner</th><th className="font-normal text-right">Accounts</th><th className="font-normal text-right">Earned</th></tr></thead>
              <tbody>
                {stats.top.map((p) => (
                  <tr key={p.partnerUid} className="border-t border-slate-100">
                    <td className="py-1">{p.company} <span className="text-slate-500">({p.code}{p.status === "suspended" ? ", paused" : ""})</span></td>
                    <td className="text-right">{p.signups}</td>
                    <td className="text-right">{formatMoney(p.earned)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className={card}>
          <h2 className="text-lg font-bold text-slate-900">Latest activity</h2>
          {stats.recent.length === 0 ? <p className="text-slate-600 text-sm mt-2">Nothing yet. Activity is recorded from now on.</p> : (
            <ul className="mt-2 space-y-1 text-sm max-h-64 overflow-y-auto">
              {stats.recent.map((e, i) => (
                <li key={i} className="text-slate-800">
                  <span className="text-slate-500">{new Date(e.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>{" "}
                  {e.type === "removed" || e.type === "applied" || e.type === "approved" ? <strong>{e.detail || e.code || "A partner"}</strong> : <strong>{e.code ?? "A partner"}</strong>}{" "}
                  {SENTENCE[e.type]}{e.type === "payment" && e.detail ? ` ($${e.detail})` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};

export default ReferralDashboard;
